import React, { useState } from 'react'
import PropTypes from 'prop-types'

const templates = [
  {
    id: 'template-1',
    name: 'Presentación Profesional',
    type: 'presentation',
    description: 'Diseño minimalista ideal para ejecutivos y freelancers',
    preview: '/dkardCMS1.png',
    features: ['Diseño limpio', 'Foto de perfil', 'Redes sociales', 'Contacto directo'],
    color: '#2563eb'
  },
  {
    id: 'template-2',
    name: 'Servicios Premium',
    type: 'services',
    description: 'Perfecto para profesionales de servicios',
    preview: '/dkardCMS3.png',
    features: ['Lista de servicios', 'Precios', 'Citas online', 'Testimonios'],
    color: '#10b981'
  },
  {
    id: 'template-3',
    name: 'Landing Personal',
    type: 'landing',
    description: 'One-page completa con todos tus datos',
    preview: '/dkardCMS4.png',
    features: ['Hero section', 'Galería', 'Formulario contacto', 'Animaciones'],
    color: '#f59e0b'
  },
  {
    id: 'template-4',
    name: 'Catálogo de Productos',
    type: 'catalog',
    description: 'Ideal para negocios con productos',
    preview: '/portada.png',
    features: ['Grid de productos', 'Carrito', 'Pasarela pago', 'Inventario'],
    color: '#ec4899'
  }
]

function TemplateSelector({ selectedTemplate, onTemplateChange }) {
  const [hoveredTemplate, setHoveredTemplate] = useState(null)

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {templates.map((template) => (
          <div
            key={template.id}
            className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all duration-200 ${
              selectedTemplate === template.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => onTemplateChange(template.id)}
            onMouseEnter={() => setHoveredTemplate(template.id)}
            onMouseLeave={() => setHoveredTemplate(null)}
          >
            {/* Selection indicator */}
            {selectedTemplate === template.id && (
              <div className="absolute top-2 right-2">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            )}

            {/* Template preview */}
            <div className="aspect-w-3 aspect-h-4 mb-4">
              <div
                className="w-full h-32 rounded-lg flex items-center justify-center text-white font-semibold"
                style={{
                  backgroundImage: `linear-gradient(135deg, ${template.color}, #05C0FB)`,
                }}
              >
                <div className="text-center">
                  <div className="text-xs opacity-80">Plantilla</div>
                  <div className="text-sm">{template.name}</div>
                </div>
              </div>
            </div>

            {/* Template info */}
            <h3 className="font-semibold text-gray-900 mb-2">{template.name}</h3>
            <p className="text-sm text-gray-600 mb-3">{template.description}</p>

            {/* Features */}
            <div className="space-y-1">
              {template.features.slice(0, 3).map((feature, index) => (
                <div key={index} className="flex items-center text-xs text-gray-500">
                  <div 
                    className="w-2 h-2 rounded-full mr-2"
                    style={{ backgroundColor: template.color }}
                  ></div>
                  {feature}
                </div>
              ))}
            </div>

            {/* Hover overlay */}
            {hoveredTemplate === template.id && (
              <div className="absolute inset-0 bg-black bg-opacity-75 rounded-xl flex items-center justify-center transition-opacity">
                <div className="text-center text-white">
                  <p className="font-semibold mb-2">{template.name}</p>
                  <p className="text-sm mb-4">{template.description}</p>
                  <div className="space-y-1 text-xs">
                    {template.features.map((feature, index) => (
                      <div key={index}>• {feature}</div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Template details */}
      {selectedTemplate && (
        <div className="bg-gray-50 rounded-xl p-6">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Plantilla seleccionada: {templates.find(t => t.id === selectedTemplate)?.name}
              </h4>
              <p className="text-gray-600 mb-4">
                {templates.find(t => t.id === selectedTemplate)?.description}
              </p>
              <div className="flex flex-wrap gap-2">
                {templates.find(t => t.id === selectedTemplate)?.features.map((feature, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-white rounded-full text-sm text-gray-700 border"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
            <div 
              className="w-16 h-16 rounded-lg flex-shrink-0"
              style={{ backgroundColor: templates.find(t => t.id === selectedTemplate)?.color }}
            ></div>
          </div>
        </div>
      )}
    </div>
  )
}

TemplateSelector.propTypes = {
  selectedTemplate: PropTypes.string.isRequired,
  onTemplateChange: PropTypes.func.isRequired,
}

export default TemplateSelector
